/**
 * Constructor collection hotspots renderer.
 *
 * Converts `refined_content` hotspot payloads into in-grid hotspot cards,
 * including shop-the-look triggers and custom video controls.
 */
// @ts-nocheck

let hotspotVideoControlId = 0;

const PLAY_BUTTON_ICON_SVG =
  '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><circle opacity="0.5" cx="10" cy="10" r="10" fill="white"></circle><path d="M8 13.3333V6.66666L13.3333 9.5238L8 13.3333Z" fill="black"></path></svg>';
const PAUSE_BUTTON_ICON_SVG =
  '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><circle opacity="0.5" cx="10" cy="10" r="10" fill="white"></circle><path d="M7.77783 6.66667V13.3333" stroke="black"></path><path d="M12.2222 6.66667V13.3333" stroke="black"></path></svg>';
const UNMUTE_ICON_SVG =
  '<svg class="icon icon__unmute" width="16" height="15" viewBox="0 0 16 15" xmlns="http://www.w3.org/2000/svg"><g><path d="M9.403 1.582c3.147 0 5.694 2.561 5.694 5.725 0 3.164-2.547 5.725-5.694 5.725" stroke="#fff" stroke-miterlimit="10" fill="none"></path><path d="M9.403 5.042a2.26 2.26 0 0 1 2.253 2.265 2.26 2.26 0 0 1-2.253 2.265" stroke="#fff" stroke-miterlimit="10" fill="none"></path><path d="M0 7.855v2.463h4.004L7.5 14.29V.44L4.004 4.404H0v2.462" fill="#fff"></path><path d="M2 1.5 15 14" stroke="#fff" fill="none"></path></g></svg>';
const MUTE_ICON_SVG =
  '<svg class="icon icon__mute hidden" width="16" height="15" viewBox="0 0 16 15" xmlns="http://www.w3.org/2000/svg"><g><path d="M9.403 1.582c3.147 0 5.694 2.561 5.694 5.725 0 3.164-2.547 5.725-5.694 5.725" stroke="#fff" stroke-miterlimit="10" fill="none"></path><path d="M9.403 5.042a2.26 2.26 0 0 1 2.253 2.265 2.26 2.26 0 0 1-2.253 2.265" stroke="#fff" stroke-miterlimit="10" fill="none"></path><path d="M0 7.855v2.463h4.004L7.5 14.29V.44L4.004 4.404H0v2.462" fill="#fff"></path></g></svg>';
const HOTSPOT_PRODUCT_FIELDS = ['product_1', 'product_2', 'product_3', 'product_4'];
const HOTSPOT_DOUBLE_WIDTH_CLASSES = [
  'collection-hotspot--width-2-up',
  'collection-hotspot--width-4-up',
  'collection-hotspot--width-page-break',
];
const FIT_GUIDE_FETCH_SECTION_ID = 'fit-guide-drawer-fetch';
const FIT_GUIDE_PRODUCT_FETCH_SECTION_ID = 'fit-guide-drawer-product-fetch';
const FIT_GUIDE_PRODUCT_HANDLE_LIMIT = 16;
const FIT_GUIDE_PRODUCT_FALLBACK_CONCURRENCY = 4;
const FIT_GUIDE_TRIGGER_SELECTOR = '[data-constructor-fit-guide-trigger][data-fit-guide-handle]';
const HOTSPOT_MEDIA_CLICK_SELECTOR = '.collection-hotspot__media[data-hotspot-media-action]';
const FIT_GUIDE_MARKUP_CACHE_MAX = 64;
const FIT_GUIDE_PRODUCT_CACHE_MAX = 128;
const HOTSPOT_LEFT_ALIGNMENT_ALIASES = new Set([
  'left',
  'center-left',
  'center left',
  'bottom-left',
  'bottom left',
]);
const HOTSPOT_CENTER_ALIGNMENT_ALIASES = new Set([
  'center',
  'bottom',
  'bottom-center',
  'bottom center',
]);

const fitGuideMarkupCache = new Map();
const fitGuideFetchPromises = new Map();
const fitGuideProductFetchCache = new Map();
const fitGuideProductFetchPromises = new Map();

function hasActiveFilters() {
  const params = new URLSearchParams(window.location.search);
  let found = false;
  params.forEach((_, key) => {
    if (!key.startsWith('filter.')) return;
    // group_id is the base browse context for collection pages, not a user-applied facet.
    if (key === 'filter.group_id') return;
    found = true;
  });
  return found;
}

function parsePositiveInt(value, fallback) {
  const parsed = parseInt(String(value || ''), 10);
  if (Number.isFinite(parsed) && parsed > 0) return parsed;
  return fallback;
}

function toHandle(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function toTextValue(value, fallback) {
  if (typeof value !== 'string') return fallback || '';
  const trimmed = value.trim();
  return trimmed || fallback || '';
}

function readFirstDefinedValue(source, keys, fallback) {
  if (!source || typeof source !== 'object' || !Array.isArray(keys)) return fallback;
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    const value = source[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return fallback;
}

function readTextValue(source, keys, fallback) {
  if (!source || typeof source !== 'object' || !Array.isArray(keys)) return fallback || '';
  for (let i = 0; i < keys.length; i++) {
    const value = toTextValue(source[keys[i]], '');
    if (value) return value;
  }
  return fallback || '';
}

function parseHotspotPosition(source, keys, fallback) {
  return parsePositiveInt(readFirstDefinedValue(source, keys, fallback), fallback);
}

function resolveHotspotLinkUrl(hotspotData) {
  const rawLink = hotspotData?.link;
  const linkFromObject = rawLink && typeof rawLink === 'object' ? rawLink.url : rawLink;
  return toTextValue(
    linkFromObject || hotspotData?.url || hotspotData?.ctaLink || hotspotData?.cta_link,
    '',
  );
}

function getLoadingPriority(position) {
  if (position <= 4) return { loading: 'eager', fetchPriority: 'high' };
  return { loading: 'lazy', fetchPriority: 'auto' };
}

function resolveRefinedContent(refinedContentRaw) {
  if (Array.isArray(refinedContentRaw)) return refinedContentRaw;
  if (refinedContentRaw && Array.isArray(refinedContentRaw.data)) return refinedContentRaw.data;
  if (refinedContentRaw && Array.isArray(refinedContentRaw.content))
    return refinedContentRaw.content;
  return [];
}

function removeExistingHotspots(grid) {
  grid.querySelectorAll('.product-grid__item--hotspot').forEach((existingHotspot) => {
    existingHotspot.remove();
  });
}

function insertHotspotsByDesktopPosition(grid, hotspots) {
  const byPosDesc = hotspots.slice().sort((a, b) => {
    return (
      (parseInt(b.dataset.positionDesktop, 10) || 0) -
      (parseInt(a.dataset.positionDesktop, 10) || 0)
    );
  });

  byPosDesc.forEach((hotspotEl) => {
    const pos = parseInt(hotspotEl.dataset.positionDesktop, 10) || 0;
    const target = grid.children[pos];
    if (target) {
      grid.insertBefore(hotspotEl, target);
    } else {
      grid.appendChild(hotspotEl);
    }
  });
}

function isDoubleWidthHotspot(hotspotElement) {
  return HOTSPOT_DOUBLE_WIDTH_CLASSES.some((className) =>
    hotspotElement.classList.contains(className),
  );
}

function buildHotspotSlots(children) {
  const hotspotSlots = [];

  children.forEach((el) => {
    if (!el.classList.contains('product-grid__item--hotspot')) return;
    const spans2 = isDoubleWidthHotspot(el);
    let pos = parseInt(el.dataset.positionMobile, 10);
    if (isNaN(pos)) return;
    if (spans2 && pos % 2 !== 0) pos--;
    hotspotSlots.push({ el, pos, spans2 });
  });

  hotspotSlots.sort((a, b) => a.pos - b.pos);
  return hotspotSlots;
}

function applyHotspotMobileOrder(hotspotSlot, mobileOrder) {
  if (hotspotSlot.spans2 && mobileOrder % 2 !== 0) {
    mobileOrder++;
  }
  hotspotSlot.el.style.setProperty('--order-mobile', String(mobileOrder));
  return mobileOrder + (hotspotSlot.spans2 ? 2 : 1);
}

function applyGridOrderVariables(grid) {
  const children = Array.from(grid.children);
  const hotspotSlots = buildHotspotSlots(children);
  const productQueue = children.filter(
    (el) => !el.classList.contains('product-grid__item--hotspot'),
  );

  let productIdx = 0;
  let hotspotIdx = 0;
  let mobileOrder = 0;

  while (hotspotIdx < hotspotSlots.length || productIdx < productQueue.length) {
    if (hotspotIdx < hotspotSlots.length && mobileOrder >= hotspotSlots[hotspotIdx].pos) {
      mobileOrder = applyHotspotMobileOrder(hotspotSlots[hotspotIdx], mobileOrder);
      hotspotIdx++;
      continue;
    }

    if (productIdx < productQueue.length) {
      productQueue[productIdx]?.style.setProperty('--order-mobile', String(mobileOrder));
      mobileOrder++;
      productIdx++;
      continue;
    }

    mobileOrder = applyHotspotMobileOrder(hotspotSlots[hotspotIdx], mobileOrder);
    hotspotIdx++;
  }

  children.forEach((el, index) => {
    el.style.setProperty('--order-desktop', String(index));
  });
}

function normalizePlacement(value) {
  const normalized = toTextValue(value, 'Center').toLowerCase();
  if (normalized === 'bottom' || normalized === 'bottom-center' || normalized === 'bottom center')
    return 'Bottom';
  if (normalized === 'bottom-left' || normalized === 'bottom left') return 'Bottom';
  return 'Center';
}

function normalizeAlignment(value, fallback = 'center') {
  const normalized = toTextValue(value, '').toLowerCase();
  if (!normalized) return fallback;
  if (HOTSPOT_LEFT_ALIGNMENT_ALIASES.has(normalized)) return 'left';
  if (HOTSPOT_CENTER_ALIGNMENT_ALIASES.has(normalized)) return 'center';
  return fallback;
}

function getPlacementDefaultAlignment(value) {
  return normalizeAlignment(value, 'center');
}

function normalizeHotspotAlignment(value, fallback = 'center') {
  return normalizeAlignment(value, fallback);
}

function inferMediaType(url, explicitType) {
  const normalizedExplicitType = toTextValue(explicitType, '').toLowerCase();
  if (normalizedExplicitType) return normalizedExplicitType;
  const normalizedUrl = toTextValue(url, '').toLowerCase();
  if (normalizedUrl.includes('.mp4')) return 'video';
  return 'image';
}

function extractHandleFromUrl(value) {
  const text = toTextValue(value, '');
  if (!text) return '';
  const match = text.match(/\/products\/([^/?#]+)/i);
  if (!match || !match[1]) return '';
  try {
    return toHandle(decodeURIComponent(match[1]));
  } catch {
    return toHandle(match[1]);
  }
}

function extractProductHandle(value) {
  if (typeof value === 'string') {
    const fromUrl = extractHandleFromUrl(value);
    return fromUrl || toHandle(value);
  }
  if (!value || typeof value !== 'object') return '';

  const objectHandle = toTextValue(value.handle, '');
  if (objectHandle) return toHandle(objectHandle);

  const objectValue = toTextValue(value.value, '');
  if (objectValue) {
    const valueHandleFromUrl = extractHandleFromUrl(objectValue);
    return valueHandleFromUrl || toHandle(objectValue);
  }

  const objectUrlHandle = extractHandleFromUrl(value.url);
  if (objectUrlHandle) return objectUrlHandle;
  return '';
}

function extractHotspotProductHandles(hotspotData) {
  const handles = [];
  const seen = Object.create(null);
  HOTSPOT_PRODUCT_FIELDS.forEach((fieldName) => {
    const rawValue = hotspotData[fieldName];
    const handle = extractProductHandle(rawValue);
    if (!handle || seen[handle]) return;
    seen[handle] = true;
    handles.push(handle);
  });
  return handles.slice(0, 4);
}

function resolveHotspotMetaobjectName(hotspotData) {
  return (
    toHandle(
      hotspotData.metaobject_name ||
        hotspotData.metaobjectName ||
        hotspotData.hotspot_name ||
        hotspotData.hotspotName ||
        hotspotData.type ||
        hotspotData.id ||
        'hotspot',
    ) || 'hotspot'
  );
}

function getShopTheLookLabel(resultsList) {
  const labelFromData = resultsList?.dataset?.shopTheLookLabel?.trim();
  if (labelFromData) return labelFromData;
  return 'Shop the look';
}

function setBoundedCacheEntry(cache, key, value, maxEntries) {
  if (!cache || !key) return;
  if (cache.has(key)) cache.delete(key);
  cache.set(key, value);
  if (cache.size <= maxEntries) return;
  const oldestKey = cache.keys().next().value;
  if (oldestKey !== undefined) {
    cache.delete(oldestKey);
  }
}

function applyFitGuideTriggerAttributes(
  element,
  fitGuideHandle,
  collectionHandle,
  ariaLabel = 'Open fit guide',
) {
  if (!(element instanceof HTMLElement)) return false;
  const normalizedFitGuideHandle = toHandle(fitGuideHandle);
  if (!normalizedFitGuideHandle) return false;

  element.dataset.constructorFitGuideTrigger = 'true';
  element.dataset.fitGuideHandle = normalizedFitGuideHandle;

  const normalizedCollectionHandle = toHandle(collectionHandle);
  if (normalizedCollectionHandle) {
    element.dataset.collectionHandle = normalizedCollectionHandle;
  } else {
    delete element.dataset.collectionHandle;
  }

  element.setAttribute('aria-label', toTextValue(ariaLabel, 'Open fit guide'));
  return true;
}

function getFitGuideFetchUrl(fitGuideHandle, collectionHandle) {
  const routeRoot = window.Shopify?.routes?.root || '/';
  const url = new URL(`${routeRoot}search`, window.location.origin);
  url.searchParams.set('section_id', FIT_GUIDE_FETCH_SECTION_ID);
  url.searchParams.set('type', 'product');
  const requestKey = [fitGuideHandle, toHandle(collectionHandle)].filter(Boolean).join(',');
  url.searchParams.set('q', requestKey || fitGuideHandle);
  return url.toString();
}

function getFitGuideProductFetchUrl(productHandle) {
  const routeRoot = window.Shopify?.routes?.root || '/';
  const url = new URL(
    `${routeRoot}products/${encodeURIComponent(productHandle)}`,
    window.location.origin,
  );
  url.searchParams.set('section_id', FIT_GUIDE_PRODUCT_FETCH_SECTION_ID);
  return url.toString();
}

function extractSectionInnerHtml(rawHtml) {
  if (!rawHtml) return '';
  const doc = new DOMParser().parseFromString(rawHtml, 'text/html');
  const wrapper = doc.querySelector('.shopify-section');
  return (wrapper ? wrapper.innerHTML : rawHtml).trim();
}

function getFitGuideCacheKey(fitGuideHandle, collectionHandle) {
  return [toHandle(fitGuideHandle), toHandle(collectionHandle)].filter(Boolean).join('::');
}

async function fetchFitGuideMarkup(fitGuideHandle, collectionHandle) {
  const handle = toHandle(fitGuideHandle);
  if (!handle) return null;
  const cacheKey = getFitGuideCacheKey(handle, collectionHandle);

  if (fitGuideMarkupCache.has(cacheKey)) return fitGuideMarkupCache.get(cacheKey) ?? null;
  if (fitGuideFetchPromises.has(cacheKey)) return fitGuideFetchPromises.get(cacheKey) || null;

  const pendingFetch = (async () => {
    try {
      const response = await fetch(getFitGuideFetchUrl(handle, collectionHandle));
      if (!response.ok) return null;
      const rawHtml = await response.text();
      const sectionHtml = extractSectionInnerHtml(rawHtml);
      if (!sectionHtml || sectionHtml.includes('data-fit-guide-fetch-empty="true"')) {
        setBoundedCacheEntry(fitGuideMarkupCache, cacheKey, null, FIT_GUIDE_MARKUP_CACHE_MAX);
        return null;
      }
      setBoundedCacheEntry(fitGuideMarkupCache, cacheKey, sectionHtml, FIT_GUIDE_MARKUP_CACHE_MAX);
      return sectionHtml;
    } catch {
      setBoundedCacheEntry(fitGuideMarkupCache, cacheKey, null, FIT_GUIDE_MARKUP_CACHE_MAX);
      return null;
    } finally {
      fitGuideFetchPromises.delete(cacheKey);
    }
  })();

  fitGuideFetchPromises.set(cacheKey, pendingFetch);
  return pendingFetch;
}

function getConstructorResultsList(collectionHandle) {
  const normalizedCollectionHandle = toHandle(collectionHandle);
  if (normalizedCollectionHandle) {
    const scopedResultsList = document.querySelector(
      `constructor-results-list[data-collection-handle="${normalizedCollectionHandle}"]`,
    );
    if (scopedResultsList instanceof Element) return scopedResultsList;
  }
  return document.querySelector('constructor-results-list');
}

function collectVisibleProductHandles(collectionHandle) {
  const resultsList = getConstructorResultsList(collectionHandle);
  if (!(resultsList instanceof Element)) return [];

  const handles = [];
  const seen = new Set();
  const links = resultsList.querySelectorAll('.product-card__link[href*="/products/"]');
  for (const link of links) {
    const href = link instanceof HTMLAnchorElement ? link.href : link.getAttribute('href');
    const handle = extractHandleFromUrl(href);
    if (!handle || seen.has(handle)) continue;
    seen.add(handle);
    handles.push(handle);
    if (handles.length >= FIT_GUIDE_PRODUCT_HANDLE_LIMIT) break;
  }
  return handles;
}

async function fetchProductFitGuideCandidate(productHandle) {
  const normalizedProductHandle = toHandle(productHandle);
  if (!normalizedProductHandle) return null;

  if (fitGuideProductFetchCache.has(normalizedProductHandle)) {
    return fitGuideProductFetchCache.get(normalizedProductHandle) ?? null;
  }
  if (fitGuideProductFetchPromises.has(normalizedProductHandle)) {
    return fitGuideProductFetchPromises.get(normalizedProductHandle) || null;
  }

  const pendingFetch = (async () => {
    try {
      const response = await fetch(getFitGuideProductFetchUrl(normalizedProductHandle));
      if (!response.ok) {
        setBoundedCacheEntry(
          fitGuideProductFetchCache,
          normalizedProductHandle,
          null,
          FIT_GUIDE_PRODUCT_CACHE_MAX,
        );
        return null;
      }

      const rawHtml = await response.text();
      if (!rawHtml || rawHtml.includes('data-fit-guide-product-fetch-empty="true"')) {
        setBoundedCacheEntry(
          fitGuideProductFetchCache,
          normalizedProductHandle,
          null,
          FIT_GUIDE_PRODUCT_CACHE_MAX,
        );
        return null;
      }

      const doc = new DOMParser().parseFromString(rawHtml, 'text/html');
      const wrapper = doc.querySelector('.shopify-section') || doc.body;
      const container = wrapper.querySelector('[data-fit-guide-product-fetch]');
      if (!(container instanceof HTMLElement)) {
        setBoundedCacheEntry(
          fitGuideProductFetchCache,
          normalizedProductHandle,
          null,
          FIT_GUIDE_PRODUCT_CACHE_MAX,
        );
        return null;
      }

      const candidateFitGuideHandle = toHandle(container.dataset.fitGuideHandle);
      const drawer = container.querySelector('#fit-guide-drawer');
      if (!(drawer instanceof HTMLElement) || !candidateFitGuideHandle) {
        setBoundedCacheEntry(
          fitGuideProductFetchCache,
          normalizedProductHandle,
          null,
          FIT_GUIDE_PRODUCT_CACHE_MAX,
        );
        return null;
      }

      const candidate = {
        fitGuideHandle: candidateFitGuideHandle,
        markup: drawer.outerHTML,
      };
      setBoundedCacheEntry(
        fitGuideProductFetchCache,
        normalizedProductHandle,
        candidate,
        FIT_GUIDE_PRODUCT_CACHE_MAX,
      );
      return candidate;
    } catch {
      setBoundedCacheEntry(
        fitGuideProductFetchCache,
        normalizedProductHandle,
        null,
        FIT_GUIDE_PRODUCT_CACHE_MAX,
      );
      return null;
    } finally {
      fitGuideProductFetchPromises.delete(normalizedProductHandle);
    }
  })();

  fitGuideProductFetchPromises.set(normalizedProductHandle, pendingFetch);
  return pendingFetch;
}

async function fetchFitGuideMarkupFromProducts(fitGuideHandle, collectionHandle) {
  const targetHandle = toHandle(fitGuideHandle);
  if (!targetHandle) return null;

  const productHandles = collectVisibleProductHandles(collectionHandle);
  if (!productHandles.length) return null;

  let resolvedMarkup = null;
  let index = 0;
  const workerCount = Math.min(FIT_GUIDE_PRODUCT_FALLBACK_CONCURRENCY, productHandles.length);

  const runWorker = async () => {
    while (!resolvedMarkup && index < productHandles.length) {
      const currentIndex = index;
      index += 1;
      const productHandle = productHandles[currentIndex];
      if (!productHandle) continue;
      const candidate = await fetchProductFitGuideCandidate(productHandle);
      if (!candidate || candidate.fitGuideHandle !== targetHandle) continue;
      resolvedMarkup = candidate.markup;
      return;
    }
  };

  await Promise.all(Array.from({ length: workerCount }, runWorker));
  return resolvedMarkup;
}

function getFitGuideHostContainer() {
  const existingHost = document.getElementById('constructor-fit-guide-fetch-host');
  if (existingHost) return existingHost;
  const host = document.createElement('div');
  host.id = 'constructor-fit-guide-fetch-host';
  document.body.appendChild(host);
  return host;
}

function mountFitGuideDrawer(sectionHtml, fitGuideHandle) {
  if (!sectionHtml) return null;

  const host = getFitGuideHostContainer();
  host.innerHTML = sectionHtml;

  const drawer = host.querySelector('#fit-guide-drawer');
  if (!(drawer instanceof HTMLElement)) {
    host.innerHTML = '';
    return null;
  }

  drawer.dataset.fitGuideHandle = fitGuideHandle;

  document.querySelectorAll('#fit-guide-drawer').forEach((existingDrawer) => {
    if (existingDrawer !== drawer) existingDrawer.remove();
  });

  window.FitGuideDrawer?.initialize?.(drawer);
  return drawer;
}

async function getOrFetchFitGuideDrawer(fitGuideHandle, collectionHandle) {
  const handle = toHandle(fitGuideHandle);
  if (!handle) return null;
  const cacheKey = getFitGuideCacheKey(handle, collectionHandle);

  const currentDrawer = document.querySelector(
    `#fit-guide-drawer[data-fit-guide-handle="${handle}"]`,
  );
  if (currentDrawer instanceof HTMLElement) return currentDrawer;

  const sectionHtml = await fetchFitGuideMarkup(handle, collectionHandle);
  if (sectionHtml) return mountFitGuideDrawer(sectionHtml, handle);

  // Fallback: resolve the fit guide via visible collection products' metafields
  // when direct handle lookup is unavailable in the search section context.
  const fallbackSectionHtml = await fetchFitGuideMarkupFromProducts(handle, collectionHandle);
  if (!fallbackSectionHtml) {
    setBoundedCacheEntry(fitGuideMarkupCache, cacheKey, null, FIT_GUIDE_MARKUP_CACHE_MAX);
    return null;
  }

  setBoundedCacheEntry(
    fitGuideMarkupCache,
    cacheKey,
    fallbackSectionHtml,
    FIT_GUIDE_MARKUP_CACHE_MAX,
  );
  return mountFitGuideDrawer(fallbackSectionHtml, handle);
}

async function onFitGuideTriggerClick(event) {
  const target = event.target instanceof Element ? event.target : null;
  const trigger = target?.closest(FIT_GUIDE_TRIGGER_SELECTOR);
  if (!(trigger instanceof HTMLElement)) return;

  event.preventDefault();
  event.stopPropagation();

  const fitGuideHandle = toHandle(trigger.dataset.fitGuideHandle);
  const collectionHandle = toHandle(trigger.dataset.collectionHandle);
  if (!fitGuideHandle || trigger.dataset.fitGuideLoading === 'true') return;

  trigger.dataset.fitGuideLoading = 'true';
  try {
    const drawer = await getOrFetchFitGuideDrawer(fitGuideHandle, collectionHandle);
    if (drawer && typeof drawer.showDialog === 'function') {
      drawer.showDialog();
    }
  } finally {
    delete trigger.dataset.fitGuideLoading;
  }
}

/**
 * Marks a hotspot media element as a click target so tapping the image/video
 * opens the same action as the on-tile control.
 *
 * @param {HTMLElement} mediaElement - The `.collection-hotspot__media` wrapper.
 * @param {'shop-the-look' | 'fit-guide' | ''} action - Which action to trigger.
 * @param {string} fitGuideHandle - Fit guide handle (used when action is fit-guide).
 * @param {string} collectionHandle - Collection handle for fit guide lookup.
 */
function applyHotspotMediaClickTarget(mediaElement, action, fitGuideHandle, collectionHandle) {
  if (!(mediaElement instanceof HTMLElement) || !action) return;

  if (action === 'fit-guide') {
    const normalizedFitGuideHandle = toHandle(fitGuideHandle);
    if (!normalizedFitGuideHandle) return;
    mediaElement.dataset.fitGuideHandle = normalizedFitGuideHandle;
    const normalizedCollectionHandle = toHandle(collectionHandle);
    if (normalizedCollectionHandle) {
      mediaElement.dataset.collectionHandle = normalizedCollectionHandle;
    }
  }

  mediaElement.dataset.hotspotMediaAction = action;
  mediaElement.classList.add('collection-hotspot__media--clickable');
}

async function openHotspotMediaFitGuide(mediaElement) {
  const fitGuideHandle = toHandle(mediaElement.dataset.fitGuideHandle);
  if (!fitGuideHandle || mediaElement.dataset.fitGuideLoading === 'true') return;

  mediaElement.dataset.fitGuideLoading = 'true';
  try {
    const drawer = await getOrFetchFitGuideDrawer(
      fitGuideHandle,
      toHandle(mediaElement.dataset.collectionHandle),
    );
    if (drawer && typeof drawer.showDialog === 'function') {
      drawer.showDialog();
    }
  } finally {
    delete mediaElement.dataset.fitGuideLoading;
  }
}

/**
 * Delegated handler that makes the whole hotspot media clickable: Shop the look
 * hotspots re-trigger their STL button, fit guide hotspots open the drawer.
 * The STL button and video controls keep managing their own clicks.
 *
 * @param {MouseEvent} event
 */
function onHotspotMediaClick(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;
  if (target.closest('.shop-the-look-button') || target.closest('.video-player__controls')) {
    return;
  }

  const mediaElement = target.closest(HOTSPOT_MEDIA_CLICK_SELECTOR);
  if (!(mediaElement instanceof HTMLElement)) return;

  const action = mediaElement.dataset.hotspotMediaAction;
  if (action === 'shop-the-look') {
    const hotspot = mediaElement.closest('.collection-hotspot');
    const stlButton = hotspot?.querySelector('.shop-the-look-button[data-shop-the-look]');
    if (stlButton instanceof HTMLElement) {
      event.preventDefault();
      stlButton.click();
    }
    return;
  }

  if (action === 'fit-guide') {
    event.preventDefault();
    void openHotspotMediaFitGuide(mediaElement);
  }
}

function createConstructorShopTheLookButton(options) {
  const handles = options.handles;
  if (!Array.isArray(handles) || handles.length === 0) return null;

  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = 'shop-the-look-button';
  trigger.dataset.productHandle = handles[0];
  trigger.dataset.shopTheLook = 'true';
  trigger.dataset.productHandles = JSON.stringify(handles);
  trigger.dataset.productCount = String(handles.length);
  trigger.dataset.requiresProductFetch = 'true';
  trigger.dataset.shopTheLookLabel = options.shopTheLookLabel || 'Shop the look';
  trigger.dataset.miniPdpSource = 'shop-the-look';
  trigger.setAttribute('on:click', '#mini-pdp-drawer/open');
  trigger.setAttribute('aria-label', trigger.dataset.shopTheLookLabel);
  trigger.textContent = trigger.dataset.shopTheLookLabel;

  const collectionHandle = toHandle(options.collectionHandle);
  const metaobjectName = toHandle(options.metaobjectName);
  if (collectionHandle && metaobjectName) {
    const plpSuffix = '_' + collectionHandle + '_' + metaobjectName;
    trigger.dataset.lineItemPropertyKey = '_plpHotspot';
    trigger.dataset.lineItemPropertySkuSuffix = plpSuffix;
  }

  return trigger;
}

function createHotspotImage(url, altText, loading, fetchPriority) {
  const image = document.createElement('img');
  image.className = 'collection-hotspot__img';
  image.src = url;
  image.alt = altText;
  image.loading = loading;
  image.setAttribute('fetchpriority', fetchPriority);
  image.decoding = 'async';
  return image;
}

function createHotspotVideo(url, fetchPriority) {
  const controlId = String(++hotspotVideoControlId);
  const deferredMedia = document.createElement('deferred-media');
  deferredMedia.className = 'collection-hotspot__video';
  deferredMedia.setAttribute('autoplay', '');
  deferredMedia.setAttribute('data-dy-video-autoplay', 'true');

  const controls = document.createElement('div');
  controls.className = 'video-player__controls hidden';
  controls.setAttribute('ref', 'videoControls');
  controls.setAttribute('on:click', '/stopPropagation');

  const playPauseButton = document.createElement('button');
  playPauseButton.className = 'video-player__control video-player__play-pause';
  playPauseButton.type = 'button';
  playPauseButton.setAttribute('ref', 'toggleMediaButton');
  playPauseButton.setAttribute('on:click', '/toggleMedia');
  playPauseButton.innerHTML =
    '<span class="visually-hidden video-player__play-text">Play video</span>' +
    '<span class="visually-hidden video-player__pause-text hidden">Pause video</span>' +
    '<span class="icon icon__play">' +
    PLAY_BUTTON_ICON_SVG +
    '</span>' +
    '<span class="icon icon__pause hidden">' +
    PAUSE_BUTTON_ICON_SVG +
    '</span>';
  controls.appendChild(playPauseButton);

  const progressBar = document.createElement('div');
  progressBar.className = 'video-player__progress-bar';
  progressBar.setAttribute('ref', 'progressBar');
  progressBar.innerHTML = `<label class="visually-hidden" for="video-progress-hotspot-${controlId}">Video progress bar</label><input id="video-progress-hotspot-${controlId}" class="video-player__progress-input" type="range" min="0" max="100" value="0" step="0.1" ref="progressInput" on:input="/seekVideo"><div class="video-player__progress-track"><div class="video-player__progress-fill" ref="progressFill" style="width: 0%;"></div><div class="video-player__progress-thumb" ref="progressThumb" style="left: 0%;"></div></div><span class="video-player__timestamp" ref="timestamp">0:00</span>`;
  controls.appendChild(progressBar);

  const muteButton = document.createElement('button');
  muteButton.className = 'video-player__control video-player__mute';
  muteButton.type = 'button';
  muteButton.setAttribute('ref', 'muteButton');
  muteButton.setAttribute('on:click', '/toggleMute');
  muteButton.innerHTML =
    '<span class="visually-hidden video-player__unmute-text">Unmute video</span>' +
    '<span class="visually-hidden video-player__mute-text hidden">Mute video</span>' +
    UNMUTE_ICON_SVG +
    MUTE_ICON_SVG;
  controls.appendChild(muteButton);
  deferredMedia.appendChild(controls);

  const video = document.createElement('video');
  video.autoplay = true;
  video.loop = true;
  video.muted = true;
  video.playsInline = true;
  video.className = 'collection-hotspot__video-element';
  video.setAttribute('preload', 'metadata');
  video.setAttribute('aria-label', 'Collection hotspot video');
  video.setAttribute('fetchpriority', fetchPriority || 'auto');

  const source = document.createElement('source');
  source.src = url;
  source.type = 'video/mp4';
  video.appendChild(source);
  deferredMedia.appendChild(video);

  return deferredMedia;
}

function createHotspotMedia(
  url,
  linkUrl,
  mediaClassName,
  altText,
  loading,
  fetchPriority,
  mediaType,
) {
  const media = document.createElement('div');
  media.className = mediaClassName;
  if (!url) return media;

  const node =
    mediaType === 'video'
      ? createHotspotVideo(url, fetchPriority)
      : createHotspotImage(url, altText, loading, fetchPriority);
  if (!linkUrl) {
    media.appendChild(node);
    return media;
  }

  const link = document.createElement('a');
  link.className = 'collection-hotspot__link';
  link.href = linkUrl;
  link.appendChild(node);
  media.appendChild(link);
  return media;
}

function createConstructorHotspotElement(
  rawHotspot,
  currentPage,
  collectionHandle,
  shopTheLookLabel,
) {
  if (!rawHotspot || typeof rawHotspot !== 'object') return null;
  const hotspotData =
    rawHotspot.data && typeof rawHotspot.data === 'object' ? rawHotspot.data : rawHotspot;

  const mediaDesktop = readTextValue(hotspotData, ['assetUrl', 'asset_url'], '');
  const mediaMobile = readTextValue(
    hotspotData,
    ['mobileAssetUrl', 'mobile_asset_url'],
    mediaDesktop,
  );
  const mediaTypeDesktop = inferMediaType(
    mediaDesktop,
    readTextValue(hotspotData, ['media_type', 'mediaType'], ''),
  );
  const mediaTypeMobile = inferMediaType(
    mediaMobile || mediaDesktop,
    readTextValue(
      hotspotData,
      ['mobile_media_type', 'mobileMediaType', 'media_type', 'mediaType'],
      '',
    ),
  );
  if (!mediaDesktop && !mediaMobile) return null;

  const pageVal = parseHotspotPosition(hotspotData, ['page'], 1);
  if (pageVal !== currentPage) return null;

  const posDesktop = parseHotspotPosition(hotspotData, ['position_desktop', 'positionDesktop'], 1);
  const posMobile = parseHotspotPosition(hotspotData, ['position_mobile', 'positionMobile'], 1);
  const widthHandle = toHandle(readTextValue(hotspotData, ['width'], '1-up')) || '1-up';
  const ctaUrl = readTextValue(hotspotData, ['ctaLink', 'cta_link'], '');
  const ctaText = readTextValue(hotspotData, ['ctaText', 'cta_text'], '');
  const text = readTextValue(hotspotData, ['header'], '');
  const body = readTextValue(hotspotData, ['body'], '');
  const textColor = readTextValue(hotspotData, ['textColor', 'text_color'], '#FFFFFF');
  const ctaColor = readTextValue(hotspotData, ['ctaColor', 'cta_color'], '#FFFFFF');
  const productHandles = extractHotspotProductHandles(hotspotData);
  const fitGuideHandle = toHandle(readTextValue(hotspotData, ['fitGuide', 'fit_guide'], ''));
  const hasShopTheLookProducts = productHandles.length > 0;
  const openStlOnHotspotClick = hasShopTheLookProducts && !ctaUrl;
  const isFitGuideActive = Boolean(fitGuideHandle && !ctaUrl);
  let mediaClickAction = '';
  if (hasShopTheLookProducts) {
    mediaClickAction = 'shop-the-look';
  } else if (isFitGuideActive) {
    mediaClickAction = 'fit-guide';
  }
  const placementSource = readFirstDefinedValue(hotspotData, ['placement', 'alignment'], '');
  const sharedPlacement = normalizePlacement(placementSource);
  const defaultAlignment = getPlacementDefaultAlignment(placementSource);
  const alignmentDesktop = normalizeHotspotAlignment(
    readFirstDefinedValue(hotspotData, ['alignment_desktop', 'alignmentDesktop'], ''),
    defaultAlignment,
  );
  const alignmentMobile = normalizeHotspotAlignment(
    readFirstDefinedValue(hotspotData, ['alignment_mobile', 'alignmentMobile'], ''),
    defaultAlignment,
  );
  const linkUrl = resolveHotspotLinkUrl(hotspotData);
  const altText = text || ctaText || 'Collection hotspot';
  const desktopMediaFetch = getLoadingPriority(posDesktop);
  const mobileMediaFetch = getLoadingPriority(posMobile);

  const hotspot = document.createElement('li');
  hotspot.className = [
    'product-grid__item',
    'product-grid__item--hotspot',
    'collection-hotspot',
    `collection-hotspot--width-${widthHandle}`,
    openStlOnHotspotClick ? 'collection-hotspot--open-stl-on-click' : '',
  ]
    .filter(Boolean)
    .join(' ');
  hotspot.dataset.positionDesktop = String(posDesktop);
  hotspot.dataset.positionMobile = String(posMobile);
  hotspot.dataset.page = String(pageVal);
  hotspot.style.setProperty('--order-desktop', String(posDesktop));
  hotspot.style.setProperty('--order-mobile', String(posMobile));
  hotspot.setAttribute('ref', 'cards[]');

  const inner = document.createElement('div');
  inner.className =
    'collection-hotspot__inner product-grid__card' +
    (hasShopTheLookProducts ? ' collection-hotspot__inner--has-stl' : '');

  if (hasShopTheLookProducts) {
    const stlButton = createConstructorShopTheLookButton({
      handles: productHandles,
      collectionHandle,
      metaobjectName: resolveHotspotMetaobjectName(hotspotData),
      shopTheLookLabel,
    });
    if (stlButton) {
      inner.appendChild(stlButton);
    }
  }

  if (text || body || ctaText) {
    const content = document.createElement('div');
    content.className = [
      'collection-hotspot__content',
      `collection-hotspot__content--placement-${sharedPlacement}`,
      `collection-hotspot__content--mobile-alignment-${alignmentMobile}`,
      `collection-hotspot__content--desktop-alignment-${alignmentDesktop}`,
    ].join(' ');

    if (text) {
      const textNode = document.createElement('div');
      textNode.className = 'collection-hotspot__text h2';
      textNode.style.color = textColor;
      textNode.textContent = text;
      content.appendChild(textNode);
    }

    if (body) {
      const bodyNode = document.createElement('div');
      bodyNode.className = 'collection-hotspot__body';
      bodyNode.style.color = textColor;
      bodyNode.textContent = body;
      content.appendChild(bodyNode);
    }

    if (ctaText) {
      const cta = document.createElement(isFitGuideActive ? 'button' : 'a');
      cta.className = 'collection-hotspot__cta button';
      if (isFitGuideActive) {
        cta.type = 'button';
        applyFitGuideTriggerAttributes(cta, fitGuideHandle, collectionHandle, ctaText);
      } else {
        cta.href = ctaUrl;
      }
      cta.style.borderColor = ctaColor;
      cta.style.color = ctaColor;
      cta.textContent = ctaText;
      content.appendChild(cta);
    }

    inner.appendChild(content);
  }

  [
    {
      className: 'collection-hotspot__media collection-hotspot__media--desktop',
      url: mediaDesktop,
      mediaType: mediaTypeDesktop,
      loading: desktopMediaFetch.loading,
      fetchPriority: desktopMediaFetch.fetchPriority,
    },
    {
      className: 'collection-hotspot__media collection-hotspot__media--mobile',
      url: mediaMobile || mediaDesktop,
      mediaType: mediaTypeMobile,
      loading: mobileMediaFetch.loading,
      fetchPriority: mobileMediaFetch.fetchPriority,
    },
  ].forEach((mediaConfig) => {
    const mediaElement = createHotspotMedia(
      mediaConfig.url,
      linkUrl,
      mediaConfig.className,
      altText,
      mediaConfig.loading,
      mediaConfig.fetchPriority,
      mediaConfig.mediaType,
    );
    applyHotspotMediaClickTarget(mediaElement, mediaClickAction, fitGuideHandle, collectionHandle);
    inner.appendChild(mediaElement);
  });

  hotspot.appendChild(inner);
  return hotspot;
}

function insertHotspots(evt) {
  const eventTarget = evt && evt.target instanceof Element ? evt.target : null;
  let resultsList =
    eventTarget && eventTarget.closest ? eventTarget.closest('constructor-results-list') : null;
  if (!resultsList) {
    resultsList = document.querySelector('constructor-results-list');
  }
  if (!resultsList || hasActiveFilters()) return;

  const grid = resultsList.querySelector('[ref="grid"]');
  if (!(grid instanceof HTMLElement)) return;

  removeExistingHotspots(grid);

  const detail = evt && evt.detail ? evt.detail : {};
  const refinedContent = resolveRefinedContent(detail.refinedContent);
  if (!refinedContent.length) return;

  const pageFromEvent = parsePositiveInt(detail.currentPage, 1);
  const collectionHandle = resultsList.dataset.collectionHandle || '';
  const currentPage = parsePositiveInt(
    new URLSearchParams(window.location.search).get('page') || pageFromEvent,
    pageFromEvent,
  );
  const shopTheLookLabel = getShopTheLookLabel(resultsList);

  const hotspotsForPage = [];
  refinedContent.forEach((hotspot) => {
    const hotspotElement = createConstructorHotspotElement(
      hotspot,
      currentPage,
      collectionHandle,
      shopTheLookLabel,
    );
    if (hotspotElement) hotspotsForPage.push(hotspotElement);
  });

  if (!hotspotsForPage.length) return;
  insertHotspotsByDesktopPosition(grid, hotspotsForPage);
  applyGridOrderVariables(grid);
}

function bindConstructorCollectionHotspots() {
  if (window.__constructorCollectionHotspotsBound) return;
  window.__constructorCollectionHotspotsBound = true;
  document.addEventListener('cnstrc:slots-rendered', insertHotspots);
  document.addEventListener('cnstrc:products-rendered', insertHotspots);
  document.addEventListener('click', onFitGuideTriggerClick);
  document.addEventListener('click', onHotspotMediaClick);

  const existingResultsList = document.querySelector('constructor-results-list');
  const latestPayload = /** @type {any} */ (window).__cnstrcHotspotPayload;
  if (existingResultsList instanceof Element && latestPayload) {
    insertHotspots({
      target: existingResultsList,
      detail: latestPayload,
    });
  }
}

bindConstructorCollectionHotspots();
